import {
  Box,
  Button,
  Container,
  Paper,
  Typography,
  TextField,
  Snackbar,
  Alert,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  RadioGroup,
  FormControlLabel,
  Radio,
  SelectChangeEvent,
  Checkbox,
  Switch,
  Divider,
  Menu,
  Tooltip,
  IconButton,
  AlertColor,
  Grid,
  FormHelperText
} from '@mui/material';
import { useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import axios from 'axios';
import { useSnackbar } from 'notistack';
import React, { useState, useEffect, ReactNode, useCallback } from 'react';
import ReactConfetti from 'react-confetti';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation } from 'react-router-dom';

import { API_BASE_URL } from '../../config/constants';
import { useAuth } from '../../contexts/Auth';
import { useCurrencies } from '../../hooks/useCurrencies';
import { useCountriesWithToken, useStatesWithToken, useHierarchyWithToken } from '../../hooks/useLocation';
import { useTaxOffices } from '../../hooks/useTaxOffices';
import { customerApi } from '../../services/api';
import CustomerPropertiesModal from '../../components/customers/CustomerPropertiesModal';
import { addCustomerAddress, addCustomerCommunication, addCustomerContact } from '../../services/customerApiHelpers';

import { customerService } from '../../services/customerService';
import '../../styles/forms.css';
import { qrCustomerService } from '../../services/qrCustomerService';
// import CustomerCreate from './CustomerCreate';

import { DEFAULT_CUSTOMER_PROPERTIES, resolveLookupDefaults, resolveDefaultSalesmanCode } from '../../services/customerPropertiesDefaults';
import { saveCustomerProperties } from '../../services/customerPropertiesApi';

import { validateTCNumber, validateTaxNumber } from '../../utils/validationUtils';

interface FormData {
  // CustomerCreate ile hizalı alanlar
  formType: string; // 'detailed' | 'quick'
  customerType: string; // '1' bireysel, '3' kurumsal
  isIndividual: boolean;
  country: string;
  region: string;
  city: string;
  district: string;
  taxOffice: string;
  taxNumber: string;
  identityNum: string; // CustomerCreate'teki isim
  firstName: string;
  lastName: string;
  customerName: string;
  address: string;
  authPhone: string;
  accountingPhone: string;
  authEmail: string;
  accountingEmail: string;
  contactName: string;
  isSubjectToEInvoice: boolean;
  eInvoiceStartDate: Date | null;
  isSubjectToEDispatch: boolean;
  eDispatchStartDate: Date | null;
  exchangeTypeCode: string;
  // Teslimat adresi alanları
  isShippingSameAsBilling: boolean;
  shippingRegion: string;
  shippingCity: string;
  shippingDistrict: string;
  shippingAddress: string;
  // Geriye dönük/yardımcı alanlar (mevcut akış bozulmasın diye tutulur)
  companyName?: string;
  email?: string;
  phone?: string;
  // token bazlı lokasyon hook'larıyla uyum için
  stateCode?: string;
  cityCode?: string;
  districtCode?: string;
  // alternatif isimler (okuma sırasında senkronize edilecek)
  identityNumber?: string;
  isSubjectToEShipment?: boolean;
  eShipmentStartDate?: Date | null;
  termsAccepted?: boolean;
  contactTitle?: string;
}

interface SnackbarState {
  open: boolean;
  message: string;
  severity: AlertColor;
}

// Dil seçenekleri için bayrak ikonları (emoji olarak)
const languageOptions = [
  { code: 'en', name: 'English', flag: '🇬🇧' },
  { code: 'tr', name: 'Türkçe', flag: '🇹🇷' },
  { code: 'ar', name: 'العربية', flag: '🇸🇦' },
  { code: 'ru', name: 'Русский', flag: '🇷🇺' }
];

// Müşteri kayıt formu (QR kod ile)
const CustomerRegistration = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const [token, setToken] = useState<string>(queryParams.get('token') || '');
  const [tokenValid, setTokenValid] = useState<boolean>(false);
  const [tokenValidating, setTokenValidating] = useState<boolean>(true);
  const [tokenExpiryTime, setTokenExpiryTime] = useState<Date | null>(null);
  const [remainingTime, setRemainingTime] = useState<number>(600); // 10 dakika = 600 saniye
  const [forceUpdate, setForceUpdate] = useState<boolean>(false); // Dil değişikliğini zorlamak için
  
  const [showConfetti, setShowConfetti] = useState(false);
  const [windowDimensions, setWindowDimensions] = useState({
    width: window.innerWidth,
    height: window.innerHeight
  });
  // SnackbarState tipi tanımı
  interface SnackbarState {
    open: boolean;
    message: string;
    severity: AlertColor;
  }
  
  const [snackbar, setSnackbar] = useState<SnackbarState>({
    open: false,
    message: '',
    severity: 'success'
  });
  // Müşteri Özellikleri modal durumu
  const [isCustomerPropertiesModalOpen, setIsCustomerPropertiesModalOpen] = useState(false);
  const [selectedCustomerProperties, setSelectedCustomerProperties] = useState<Record<number, string>>({});

  // Başarı sesi çal
  const playSuccessSound = useCallback(() => {
    try {
      // Web Audio API kullanarak ses oluştur
      const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
      
      // Başarı melodisi için notalar (C-E-G-C arpeji)
      const notes = [261.63, 329.63, 392.00, 523.25]; // C4, E4, G4, C5 frekansları
      const noteDuration = 0.15; // Her notanın süresi (saniye)
      
      // Her nota için oscillator oluştur ve çal
      notes.forEach((freq, index) => {
        const oscillator = audioContext.createOscillator();
        const gainNode = audioContext.createGain();
        
        oscillator.type = 'sine';
        oscillator.frequency.value = freq;
        
        gainNode.gain.setValueAtTime(0.5, audioContext.currentTime);
        gainNode.gain.exponentialRampToValueAtTime(0.001, audioContext.currentTime + noteDuration);
        
        oscillator.connect(gainNode);
        gainNode.connect(audioContext.destination);
        
        oscillator.start(audioContext.currentTime + index * noteDuration);
        oscillator.stop(audioContext.currentTime + (index + 1) * noteDuration);
      });
      
      // Havai fişek sesi ekle
      setTimeout(() => {
        const fireworksOscillator = audioContext.createOscillator();
        const fireworksGain = audioContext.createGain();
        
        fireworksOscillator.type = 'sawtooth';
        fireworksOscillator.frequency.value = 150;
        fireworksOscillator.frequency.exponentialRampToValueAtTime(40, audioContext.currentTime + 0.5);
        
        fireworksGain.gain.setValueAtTime(0.3, audioContext.currentTime);
        fireworksGain.gain.exponentialRampToValueAtTime(0.001, audioContext.currentTime + 0.5);
        
        fireworksOscillator.connect(fireworksGain);
        fireworksGain.connect(audioContext.destination);
        
        fireworksOscillator.start();
        fireworksOscillator.stop(audioContext.currentTime + 0.5);
      }, notes.length * noteDuration * 1000);
      
    } catch (error) {
      console.error('Ses çalma hatası:', error);
    }
  }, []);
  
  // Hata sesi çal
  const playErrorSound = useCallback(() => {
    try {
      // Web Audio API kullanarak ses oluştur
      const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
      
      // Düşük frekanslı "aww" sesi
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();
      
      oscillator.type = 'sine';
      oscillator.frequency.value = 180;
      oscillator.frequency.exponentialRampToValueAtTime(120, audioContext.currentTime + 0.6);
      
      gainNode.gain.setValueAtTime(0.3, audioContext.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.001, audioContext.currentTime + 0.6);
      
      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);
      
      oscillator.start();
      oscillator.stop(audioContext.currentTime + 0.6);
      
    } catch (error) {
      console.error('Ses çalma hatası:', error);
    }
  }, []);
  
  // Pencere boyutlarını takip et
  useEffect(() => {
    const handleResize = () => {
      setWindowDimensions({
        width: window.innerWidth,
        height: window.innerHeight
      });
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const { isAuthenticated, login } = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  // Çeviri hook'unu kullan
  const { t, i18n } = useTranslation();
  
  // Form dili sadece Türkçe olacak şekilde sabitle
  useEffect(() => {
    try {
      if (i18n.language !== 'tr') {
        i18n.changeLanguage('tr');
      }
    } catch (err) {
      console.error('Dil sabitleme hatası:', err);
    }
  }, [i18n]);
  
  // Dil seçimi için menü durumu
  const [languageMenuAnchor, setLanguageMenuAnchor] = useState<null | HTMLElement>(null);
  const languageMenuOpen = Boolean(languageMenuAnchor);
  
  // Dil menüsünü açma/kapama işleyicileri
  const handleLanguageMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setLanguageMenuAnchor(event.currentTarget);
  };
  
  const handleLanguageMenuClose = () => {
    setLanguageMenuAnchor(null);
  };
  
  // Dil değiştirme işleyicisi
  const changeLanguage = (languageCode: string) => {
    console.log('Dil değiştiriliyor:', languageCode);
    try {
      i18n.changeLanguage(languageCode)
        .then(() => {
          console.log('Dil başarıyla değiştirildi:', languageCode);
          // Dil değişikliğini zorlamak için state güncelleme
          setForceUpdate((prev: boolean) => !prev);
        })
        .catch((error: Error) => {
          console.error('Dil değiştirme hatası:', error);
        });
    } catch (error) {
      console.error('Dil değiştirme hatası:', error);
      // Alternatif yöntem
      localStorage.setItem('i18nextLng', languageCode);
      window.location.reload();
    }
    handleLanguageMenuClose();
  };
  
  // Geçerli dili al
  const currentLanguage = languageOptions.find(lang => lang.code === i18n.language) || languageOptions[0];
  
  // Kalan süreyi takip et
  useEffect(() => {
    // Token geçerli değilse veya süre dolmuşsa zamanlayıcıyı çalıştırma
    if (!tokenValid || !tokenExpiryTime) return;
    
    const timer = setInterval(() => {
      const now = new Date();
      const remainingSecs = Math.floor((tokenExpiryTime.getTime() - now.getTime()) / 1000);
      
      if (remainingSecs <= 0) {
        // Süre doldu, token'ı geçersiz kıl
        setTokenValid(false);
        setRemainingTime(0);
        setSnackbar({
          open: true,
          message: t('customerRegistration.tokenValidation.expired'),
          severity: 'error'
        });
        clearInterval(timer);
      } else {
        // Kalan süreyi güncelle
        setRemainingTime(remainingSecs);
      }
    }, 1000); // Her saniye güncelle
    
    // Temizleme fonksiyonu
    return () => clearInterval(timer);
  }, [tokenValid, tokenExpiryTime, t]);
  
  // Kalan süreyi formatla (dk:sn)
  const formatRemainingTime = () => {
    const minutes = Math.floor(remainingTime / 60);
    const seconds = remainingTime % 60;
    return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  };

  // Form state (CustomerCreate ile birebir hizalı varsayılanlar)
  const [formData, setFormData] = useState<FormData>({
    formType: 'detailed',
    customerType: '3',
    isIndividual: false,
    country: 'TR',
    region: '',
    city: '',
    district: '',
    taxOffice: '',
    taxNumber: '',
    identityNum: '',
    firstName: '',
    lastName: '',
    customerName: '',
    address: '',
    authPhone: '',
    accountingPhone: '',
    authEmail: '',
    accountingEmail: '',
    contactName: '',
    isSubjectToEInvoice: false,
    eInvoiceStartDate: null,
    isSubjectToEDispatch: false,
    eDispatchStartDate: null,
    exchangeTypeCode: 'TRY',
    isShippingSameAsBilling: true,
    shippingRegion: '',
    shippingCity: '',
    shippingDistrict: '',
    shippingAddress: '',
    // Geriye dönük/yardımcı alanlar
    companyName: '',
    email: '',
    phone: '',
    stateCode: '',
    cityCode: '',
    districtCode: '',
    identityNumber: '',
    isSubjectToEShipment: false,
    eShipmentStartDate: null,
    termsAccepted: false,
    contactTitle: ''
  });

  // Referans veri state'leri
  const [locationHierarchy, setLocationHierarchy] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(false);
  // Not: SnackbarState tipi zaten yukarıda tanımlandı
  
  // Bölge, şehir ve ilçe listeleri için state'ler
  const [regions, setRegions] = useState<Array<{code: string, name: string}>>([]);
  const [cities, setCities] = useState<{ code: string; name: string }[]>([]);
  const [districts, setDistricts] = useState<{ code: string; name: string }[]>([]);
  // Teslimat adresi için bağımsız şehir/ilçe listeleri
  const [shippingCities, setShippingCities] = useState<{ code: string; name: string }[]>([]);
  const [shippingDistricts, setShippingDistricts] = useState<{ code: string; name: string }[]>([]);
  
  // Debug için locationHierarchyData'yı izle
  const [debugHierarchy, setDebugHierarchy] = useState<any>(null);

  // Token ile vergi dairelerini ve para birimlerini getir
  const { data: taxOfficesDataFromHook, isLoading: isLoadingTaxOfficesHook } = useTaxOffices(token, tokenValid);
  
  // Para birimlerini getiren hook'u kullan
  const { data: currenciesDataFromHook, isLoading: isLoadingCurrenciesHook } = useCurrencies(token, 'TR', tokenValid);
  
  // Ülkeleri getiren hook'u kullan
  const { data: countriesDataFromHook, isLoading: isLoadingCountriesHook } = useCountriesWithToken(token, 'TR', tokenValid);
  
  // Bölgeleri/illeri getiren hook'u kullan
  const { data: statesDataFromHook, isLoading: isLoadingStatesHook } = useStatesWithToken(token, 'TR', tokenValid);
  
  // Hiyerarşik lokasyon verisini getiren hook'u kullan - formData.stateCode değiştiğinde güncellenecek
  const { data: locationHierarchyData, isLoading: isLoadingHierarchyHook } = useHierarchyWithToken(
    token,
    'TR',
    'TR', // Her zaman TR ülke kodunu kullan
    tokenValid // Her zaman aktif olsun, bölge seçilmese bile veriyi çekelim
  );
  
  // locationHierarchyData geldikten sonra regions listesini hazırla
  useEffect(() => {
    if (locationHierarchyData && Array.isArray(locationHierarchyData.states)) {
      const stateList = locationHierarchyData.states.map((state: any) => ({
        code: state.stateCode,
        name: state.stateDescription
      }));
      setRegions(stateList);
    } else {
      setRegions([]);
    }
  }, [locationHierarchyData]);

  // Bölge (region) değişince şehir listesini güncelle
  useEffect(() => {
    if (formData.region && locationHierarchyData) {
      const selectedState = (locationHierarchyData.states || []).find(
        (state: any) => state.stateCode === formData.region
      );
      if (selectedState && Array.isArray((selectedState as any).cities)) {
        const cityList = ((selectedState as any).cities as any[]).map((city: any) => ({
          code: city.cityCode,
          name: city.cityDescription
        }));
        setCities(cityList);
      } else {
        setCities([]);
      }
    } else {
      setCities([]);
      setDistricts([]);
    }
  }, [formData.region, locationHierarchyData]);
  
  // Şehir (city) değişince ilçe listesini güncelle
  useEffect(() => {
    if (locationHierarchyData && formData.region && formData.city) {
      const selectedState = (locationHierarchyData.states || []).find(
        (state: any) => state.stateCode === formData.region
      );
      const selectedCity = (selectedState as any)?.cities?.find((city: any) => city.cityCode === formData.city);
      if (selectedCity && Array.isArray(selectedCity.districts)) {
        const districtList = selectedCity.districts.map((d: any) => ({
          code: d.districtCode,
          name: d.districtDescription
        }));
        setDistricts(districtList);
      } else {
        setDistricts([]);
      }
    } else {
      setDistricts([]);
    }
  }, [locationHierarchyData, formData.region, formData.city]);

  // Teslimat adresi için: shippingRegion değiştiğinde şehir listesini güncelle
  useEffect(() => {
    if (locationHierarchyData && formData.shippingRegion) {
      const selectedState: any = (locationHierarchyData as any).states?.find((s: any) => s.stateCode === formData.shippingRegion);
      if (selectedState && selectedState.cities && selectedState.cities.length > 0) {
        const list = (selectedState.cities as any[]).map((c: any) => ({ code: c.cityCode, name: c.cityDescription }));
        setShippingCities(list);
      } else {
        setShippingCities([]);
      }
      // Bölge değiştiğinde alt seçimleri sıfırla
      setFormData(prev => ({ ...prev, shippingCity: '', shippingDistrict: '' }));
    } else {
      setShippingCities([]);
    }
  }, [locationHierarchyData, formData.shippingRegion]);

  // Teslimat adresi için: shippingCity değiştiğinde ilçe listesini güncelle
  useEffect(() => {
    if (locationHierarchyData && formData.shippingRegion && formData.shippingCity) {
      const selectedState: any = (locationHierarchyData as any).states?.find((s: any) => s.stateCode === formData.shippingRegion);
      const selectedCity: any = selectedState?.cities?.find((c: any) => c.cityCode === formData.shippingCity);
      if (selectedCity && selectedCity.districts && selectedCity.districts.length > 0) {
        const list = (selectedCity.districts as any[]).map((d: any) => ({ code: d.districtCode, name: d.districtDescription }));
        setShippingDistricts(list);
      } else {
        setShippingDistricts([]);
      }
      // Şehir değiştiğinde ilçe seçimini sıfırla
      setFormData(prev => ({ ...prev, shippingDistrict: '' }));
    } else {
      setShippingDistricts([]);
    }
  }, [locationHierarchyData, formData.shippingRegion, formData.shippingCity]);

  // Oturum kontrolü - Token varsa login kontrolünü atla
  useEffect(() => {
    const checkAuth = async () => {
      // Token varsa login kontrolünü atla
      if (token) {
        // console.log('Token mevcut, login kontrolü atlanıyor...'); // Sürekli log'u kaldırıyoruz
        return;
      }
      
      const authToken = localStorage.getItem('accessToken') || localStorage.getItem('token');
      if (!authToken && !isAuthenticated) {
        console.log('Oturum açılmamış ve token yok, login sayfasına yönlendiriliyor...');
        try {
          // await login('admin@example.com', 'Admin123!'); // Otomatik login denemesi - Gerekirse açılabilir
        } catch (error) {
          console.error('Otomatik login hatası:', error);
          navigate('/login', { replace: true });
        }
      }
    };
    checkAuth();
    // login fonksiyonunu bağımlılık dizisinden çıkarıyoruz çünkü kullanılmıyor
  }, [isAuthenticated, navigate, token]);
  
  // Token doğrulama
  useEffect(() => {
    // Önce localStorage'dan kayıtlı token geçerlilik süresini kontrol et
    const savedTokenData = localStorage.getItem(`token_expiry_${token}`);
    
    if (savedTokenData) {
      try {
        const parsedData = JSON.parse(savedTokenData);
        const expiryTime = new Date(parsedData.expiryTime);
        const now = new Date();
        
        // Token hala geçerli mi kontrol et
        if (expiryTime > now) {
          setTokenValid(true);
          setTokenExpiryTime(expiryTime);
          const remainingSecs = Math.floor((expiryTime.getTime() - now.getTime()) / 1000);
          setRemainingTime(remainingSecs);
          setTokenValidating(false);
          return; // Eğer localStorage'dan geçerli bir token bulunduysa, API isteği yapma
        } else {
          // Süresi dolmuş tokeni localStorage'dan temizle
          localStorage.removeItem(`token_expiry_${token}`);
        }
      } catch (error) {
        console.error('Token verisi ayrıştırılırken hata oluştu:', error);
        localStorage.removeItem(`token_expiry_${token}`);
      }
    }
    
    // localStorage'da geçerli bir token yoksa, API'den doğrula
    const validateToken = async () => {
      try {
        const response = await axios.get(`${API_BASE_URL}/api/v1/Customer/validate-token/${token}`);
        
        if (response.data && response.data.success) {
          setTokenValid(true);
          
          // Token geçerlilik süresini ayarla (10 dakika)
          const expiryTime = new Date();
          expiryTime.setMinutes(expiryTime.getMinutes() + 10);
          setTokenExpiryTime(expiryTime);
          
          // Kalan süreyi hesapla (saniye cinsinden)
          const remainingSecs = Math.floor((expiryTime.getTime() - new Date().getTime()) / 1000);
          setRemainingTime(remainingSecs);
          
          // Token bilgilerini localStorage'a kaydet
          localStorage.setItem(`token_expiry_${token}`, JSON.stringify({
            expiryTime: expiryTime.toISOString(),
            tokenValid: true
          }));
          
          // Eğer token bir müşteri koduna bağlıysa, müşteri bilgilerini yükle
          if (response.data.customerCode) {
            // Burada müşteri bilgilerini yükleme işlemi yapılabilir
            console.log('Müşteri kodu:', response.data.customerCode);
          }
          
          // Başarılı doğrulama mesajı göster
          setSnackbar({
            open: true,
            message: t('customerRegistration.tokenValidation.success'),
            severity: 'success'
          });
        } else {
          setTokenValid(false);
          setTokenExpiryTime(null);
          setRemainingTime(0);
          localStorage.removeItem(`token_expiry_${token}`);
          setSnackbar({
            open: true,
            message: response.data?.message || t('customerRegistration.tokenValidation.invalid'),
            severity: 'error'
          });
        }
      } catch (error) {
        console.error('Token doğrulama hatası:', error);
        setTokenValid(false);
        setTokenExpiryTime(null);
        setRemainingTime(0);
        localStorage.removeItem(`token_expiry_${token}`);
        setSnackbar({
          open: true,
          message: t('customerRegistration.tokenValidation.invalid'),
          severity: 'error'
        });
      } finally {
        setTokenValidating(false);
      }
    };
    
    if (token) {
      validateToken();
    }
  }, [token, t]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    
    // TC Kimlik ve vergi numarası doğrulaması
    if (name === 'taxNumber' && value.length > 0) {
      // Sadece rakam girişine izin ver
      if (!/^\d*$/.test(value)) {
        return; // Rakam değilse işlemi durdur
      }
      
      // Maksimum 10 karakter sınırlaması
      if (value.length > 10) {
        return; // 10 karakterden fazla ise işlemi durdur
      }
    }
    
    if ((name === 'identityNumber' || name === 'identityNum') && value.length > 0) {
      // Sadece rakam girişine izin ver
      if (!/^\d*$/.test(value)) {
        return; // Rakam değilse işlemi durdur
      }
      
      // Maksimum 11 karakter sınırlaması
      if (value.length > 11) {
        return; // 11 karakterden fazla ise işlemi durdur
      }
    }
    
    setFormData(prev => {
      // Hızlı ekranda müşteri ünvanı değiştiğinde otomatik doldurma
       if (name === 'customerName' && prev.formType === 'quick' && value.trim() !== '') {
         return {
           ...prev,
           [name]: value,
           firstName: value, // Adı kopyala
           lastName: value, // Soyadı kopyala
           identityNumber: '22222222222', // TC otomatik yaz
           phone: '90-555-555-55-55', // Telefon otomatik yaz
           email: 'test@edikravat.tr', // Mail otomatik yaz
           address: 'Varsayılan Adres', // Adres otomatik yaz
           country: 'TR', // Ülke
           region: 'TR.MR', // Bölge
           city: 'TR.34', // Şehir
           district: 'TR.03412' // İlçe
         };
       }
      
      // Eğer müşteri adı değişiyorsa ve gerçek kişi ise, firstName alanını da güncelle
      if (name === 'customerName' && prev.isIndividual) {
        return { ...prev, [name]: value, firstName: value };
      }
      
      // Eğer firstName veya lastName değişiyorsa ve gerçek kişi ise, müşteri açıklamasını otomatik oluştur
      if ((name === 'firstName' || name === 'lastName') && prev.isIndividual) {
        const updatedData = { ...prev, [name]: value };
        // Ad ve soyadı birleştirerek müşteri açıklaması oluştur
        if (name === 'firstName' && updatedData.lastName) {
          updatedData.customerName = `${value} ${updatedData.lastName}`;
        } else if (name === 'lastName' && updatedData.firstName) {
          updatedData.customerName = `${updatedData.firstName} ${value}`;
        }
        return updatedData;
      }
      
      // identityNumber ile identityNum senkronizasyonu
      if (name === 'identityNumber') {
        return { ...prev, [name]: value, identityNum: value };
      }
      if (name === 'identityNum') {
        return { ...prev, [name]: value, identityNumber: value };
      }
      
      return { ...prev, [name]: value };
    });
  };

  const handleSelectChange = (e: SelectChangeEvent<string>) => {
    const { name, value } = e.target;
    
    // Bölge seçimi (CustomerCreate hizalı)
    if (name === 'region') {
      setFormData(prev => ({ ...prev, region: value, city: '', district: '' }));
      return;
    }
    
    // Teslimat adresi için bağımlı alanları sıfırla
    if (name === 'shippingRegion') {
      setFormData(prev => ({ ...prev, shippingRegion: value, shippingCity: '', shippingDistrict: '' }));
      return;
    }
    if (name === 'shippingCity') {
      setFormData(prev => ({ ...prev, shippingCity: value, shippingDistrict: '' }));
      return;
    }
    // Şehir seçimi
    if (name === 'city') {
      setFormData(prev => ({ ...prev, city: value, district: '' }));
      return;
    }
    // İlçe seçimi
    if (name === 'district') {
      setFormData(prev => ({ ...prev, district: value }));
      return;
    }
    // Ülke değişiminde TR dışına çıkılırsa bağlı alanları temizle
    if (name === 'country') {
      if (value !== 'TR') {
        setFormData(prev => ({
          ...prev,
          country: value,
          stateCode: '', cityCode: '', districtCode: '',
          region: '', city: '', district: '',
          shippingRegion: '', shippingCity: '', shippingDistrict: '',
        }));
        // Listeleri de temizlemek için state setter'ları çağır (harici state'ler)
        setCities([]);
        setDistricts([]);
        setShippingCities([]);
        setShippingDistricts([]);
        return;
      }
      setFormData(prev => ({ ...prev, country: value }));
      return;
    }
    
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  // Geri uyumluluk için: eğer eski kod alanları doluysa, ana alanlara kopyala
  useEffect(() => {
    setFormData(prev => {
      const updates: any = { ...prev };
      let changed = false;
      if (!prev.region && prev.stateCode) { updates.region = prev.stateCode; changed = true; }
      if (!prev.city && prev.cityCode) { updates.city = prev.cityCode; changed = true; }
      if (!prev.district && prev.districtCode) { updates.district = prev.districtCode; changed = true; }
      return changed ? updates : prev;
    });
  }, []);

  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, checked } = e.target;
    
    setFormData(prev => {
      // Gerçek kişi seçildiğinde, müşteri adını firstName alanına kopyala
      if (name === 'isIndividual' && checked) {
        return { 
          ...prev, 
          [name]: checked,
          firstName: prev.customerName // Müşteri adını firstName alanına kopyala
          //lastName: prev.customerName // Müşteri adını firstName alanına kopyala
        };
      }
      
      // E-Fatura veya E-İrsaliye checkbox'ları için tarih alanlarını ayarla
      if (name === 'isSubjectToEInvoice' && checked && !prev.eInvoiceStartDate) {
        return { ...prev, [name]: checked, eInvoiceStartDate: new Date() };
      }
      
      // E-İrsaliye (dispatch) için eski ve yeni alan adlarını birlikte yönet
      if (name === 'isSubjectToEShipment' && checked && !prev.eDispatchStartDate) {
        return { ...prev, isSubjectToEShipment: checked, isSubjectToEDispatch: checked, eShipmentStartDate: new Date(), eDispatchStartDate: new Date() };
      }
      if (name === 'isSubjectToEDispatch' && checked && !prev.eDispatchStartDate) {
        return { ...prev, isSubjectToEDispatch: checked, isSubjectToEShipment: checked, eDispatchStartDate: new Date(), eShipmentStartDate: new Date() };
      }
      
      // Diğer checkbox senaryolarında alan adları arası senkronizasyonu koru
      if (name === 'isSubjectToEShipment') {
        return { ...prev, isSubjectToEShipment: checked, isSubjectToEDispatch: checked };
      }
      if (name === 'isSubjectToEDispatch') {
        return { ...prev, isSubjectToEDispatch: checked, isSubjectToEShipment: checked };
      }
      
      return { ...prev, [name]: checked };
    });
  };

  const handleDateChange = (name: 'eInvoiceStartDate' | 'eShipmentStartDate' | 'eDispatchStartDate', value: string) => {
    if (value) {
      setFormData(prev => {
        const updated: any = { ...prev };
        if (name === 'eShipmentStartDate') {
          updated.eShipmentStartDate = new Date(value);
          updated.eDispatchStartDate = new Date(value);
        } else if (name === 'eDispatchStartDate') {
          updated.eDispatchStartDate = new Date(value);
          updated.eShipmentStartDate = new Date(value);
        } else {
          updated[name] = new Date(value);
        }
        return updated;
      });
    } else {
      setFormData(prev => {
        const updated: any = { ...prev };
        if (name === 'eShipmentStartDate') {
          updated.eShipmentStartDate = null;
          updated.eDispatchStartDate = null;
        } else if (name === 'eDispatchStartDate') {
          updated.eDispatchStartDate = null;
          updated.eShipmentStartDate = null;
        } else {
          updated[name] = null;
        }
        return updated;
      });
    }
  };

  // Form ve Snackbar fonksiyonları dosyanın sonunda tanımlanmıştır

  // Token ile müşteri oluşturma işlemi
  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    
    // Token geçerli değilse form gönderimini engelle
    if (!tokenValid || !token) {
      setSnackbar({
        open: true,
        message: 'Geçersiz kayıt linki. Lütfen geçerli bir link kullanın.',
        severity: 'error'
      });
      return;
    }
    
    // Hızlı ekranda kayıt edilirken otomatik doldurma
    if (formData.formType === 'quick' && formData.customerName && formData.customerName.trim() !== '') {
      // Boş alanları otomatik doldur
      const updatedFormData = { ...formData };
      
      if (!updatedFormData.firstName || updatedFormData.firstName.trim() === '') {
        updatedFormData.firstName = formData.customerName; // Adı kopyala
        updatedFormData.lastName = formData.customerName; // Soyadı kopyala
      }
      
      if (!updatedFormData.identityNumber || updatedFormData.identityNumber.trim() === '') {
         updatedFormData.identityNumber = '22222222222'; // TC otomatik yaz
       }
      
      if (!updatedFormData.phone || updatedFormData.phone.trim() === '') {
        updatedFormData.phone = '90-555-555-55'; // Telefon otomatik yaz
      }
      
      if (!updatedFormData.email || updatedFormData.email.trim() === '') {
        updatedFormData.email = 'test@edikravat.tr'; // Mail otomatik yaz
      }
      
      if (!updatedFormData.address || updatedFormData.address.trim() === '') {
        updatedFormData.address = 'Varsayılan Adres'; // Adres otomatik yaz
        updatedFormData.country = 'TR'; // Ülke
        updatedFormData.region = 'TR.MR'; // Bölge
        updatedFormData.city = 'TR.34'; // Şehir
        updatedFormData.district = 'TR.03412'; // İlçe
      }
      
      // FormData'yı güncelle
      setFormData(updatedFormData);
    }
    
    setIsLoading(true);
    let error: any = null;
    let errorMessage = '';

    try {
      // Temel doğrulama
      if (!formData.customerName || formData.customerName.trim() === '') {
        errorMessage = 'Müşteri adı zorunludur';
        throw new Error(errorMessage);
      }
      
      // Varsayılan değerleri belirle
      let phoneToUse = formData.phone;
      let emailToUse = formData.email;
      
      // Telefon girilmemişse varsayılan telefon
      if (!formData.phone || formData.phone.trim() === '') {
        phoneToUse = '90-555-555-55';
      }
      
      // E-mail girilmemişse varsayılan e-mail
      if (!formData.email || formData.email.trim() === '') {
        emailToUse = 'test@edikravat.tr';
      }
      
      // TC kimlik ve vergi dairesi boşsa varsayılan TC kimlik
      if (formData.country === 'TR' && formData.isIndividual && 
          (!formData.identityNumber || formData.identityNumber.trim() === '') && 
          (!formData.taxNumber || formData.taxNumber.trim() === '')) {
        formData.identityNumber = '22222222222';
      }
      
      // TC Kimlik numarası doğrulaması
      if (formData.country === 'TR' && formData.isIndividual && formData.identityNumber) {
        if (!validateTCNumber(formData.identityNumber)) {
          errorMessage = 'Geçersiz TC Kimlik numarası. TC Kimlik numarası 11 haneli sayı olmalıdır.';
          throw new Error(errorMessage);
        }
      }
      
      // Vergi numarası doğrulaması
      if (formData.country === 'TR' && !formData.isIndividual && formData.taxNumber) {
        if (!validateTaxNumber(formData.taxNumber)) {
          errorMessage = 'Geçersiz vergi numarası. Vergi numarası 10 haneli olmalı ve tüm karakterler rakam olmalıdır.';
          throw new Error(errorMessage);
        }
      }

      // Form tipine göre doğrulama
      if (formData.formType === 'detailed') {
        // Detaylı form için ek doğrulamalar
        if (formData.isIndividual) {
          // Bireysel müşteri için doğrulamalar - varsayılan değer kullanılacağı için zorunluluk kaldırıldı
          // TC kimlik numarası varsa doğrula (varsayılan değer de dahil)
          if (formData.identityNumber && !validateTCNumber(formData.identityNumber)) {
            errorMessage = 'Geçersiz TC Kimlik numarası. TC Kimlik numarası 11 haneli sayı olmalıdır.';
            throw new Error(errorMessage);
          }
        } else {
          // Kurumsal müşteri için doğrulamalar
          if (!formData.taxNumber || formData.taxNumber.trim() === '') {
            errorMessage = 'Vergi Numarası zorunludur';
            throw new Error(errorMessage);
          }
          if (!formData.taxOffice || formData.taxOffice.trim() === '') {
            errorMessage = 'Vergi Dairesi zorunludur';
            throw new Error(errorMessage);
          }
        }
      } else {
        // Hızlı form için doğrulamalar - varsayılan değerler kullanılacağı için zorunluluk kaldırıldı
        // Telefon veya e-posta zorunluluğu kaldırıldı çünkü varsayılan değerler atanacak
      }
      
      // Lookup ve satış temsilcisi varsayılanlarını belirle
      const { priceGroupCode, discountGroupCode } = resolveLookupDefaults();
      const salesmanCodeDefault = resolveDefaultSalesmanCode();

      // Backend'in beklediği CustomerData modeline uygun formatta veri hazırla
      // Önemli: Backend'deki CustomerData sınıfı alan isimleri büyük harfle başlıyor
      const customerData: any = {
        // CustomerData sınıfına uygun alanlar
        CustomerCode: "", // Boş gönder, backend otomatik oluşturacak
        CustomerName: formData.isIndividual ? (formData.firstName || 'Bireysel Müşteri') : (formData.customerName || 'Kurumsal Müşteri'), // string - Required
        CustomerSurname: formData.isIndividual ? formData.lastName : "", // Bireysel müşteri için soyad
        TitleCode: "", // Unvan kodu
        Patronym: "", // Baba adı
        TaxNumber: !formData.isIndividual ? formData.taxNumber || '' : '',
        CustomerIdentityNumber: formData.identityNumber || '22222222222', // Müşteri kimlik numarası
        IdentityNum: formData.identityNumber || '22222222222', // TC Kimlik numarası
        MersisNum: "", // Mersis numarası
        CustomerTypeCode: String(formData.isIndividual ? 1 : 3), // string - Required (1: Bireysel, 3: Kurumsal)
        IsIndividual: formData.isIndividual, // boolean
        DiscountGroupCode: discountGroupCode || "", // İndirim grubu kodu
        CustomerMarkupGrCode: priceGroupCode || "", // Müşteri markup grup kodu (fiyat grubu)
        CustomerPaymentPlanGrCode: "", // Müşteri ödeme planı grup kodu
        VendorPaymentPlanGrCode: "", // Tedarikçi ödeme planı grup kodu
        CurrencyCode: "TRY", // Para birimi kodu
        OfficeCode: "M", // string - Required - Varsayılan ofis kodu
        CompanyCode: "1", // string - Required - Şirket kodu
        SalesmanCode: salesmanCodeDefault || "", // Satış temsilcisi kodu
        DataLanguageCode: "TR", // Veri dili kodu
        CreditLimit: 0, // Kredi limiti
        RiskLimit: 0, // Risk limiti
        CityCode: formData.cityCode || formData.city || 'TR.34', // Şehir kodu
        DistrictCode: formData.districtCode || formData.district || 'TR.03412', // İlçe kodu
        TaxOfficeCode: !formData.isIndividual ? formData.taxOffice || '' : '', // Vergi dairesi kodu
        Address: formData.address || '', // Adres
        PaymentTerm: 0, // Ödeme vadesi
        
        // Ek alanlar (CustomerData sınıfında olmayabilir ama yine de gönderelim)
        email: emailToUse,
        phoneNumber: phoneToUse,
        countryCode: 'TR',
        stateCode: formData.stateCode || formData.region || 'TR.MR',
        regionCode: formData.region || 'TR.MR'
      };
      
      // Adres bilgilerini hazırla - adres girilmemişse ve Türkiye seçiliyse varsayılan adres oluştur
      let addressToUse = formData.address;
      let stateCodeToUse = formData.stateCode || formData.region;
      let cityCodeToUse = formData.cityCode || formData.city;
      let districtCodeToUse = formData.districtCode || formData.district;
      
      // Adres girilmemişse ve Türkiye seçiliyse varsayılan değerleri kullan
      if ((!formData.address || formData.address.trim() === '') && formData.country === 'TR') {
        addressToUse = 'İstanbul, Osmanbey 1';
        stateCodeToUse = 'TR.MR';
        cityCodeToUse = 'TR.34';
        districtCodeToUse = 'TR.03412';
      }
      
      // NOT: Backend'in CustomerData modeli adres bilgilerini doğrudan içermiyor
      // Adres bilgileri ayrı bir API çağrısıyla eklenecek
      // Bu nedenle customerData nesnesine adres bilgilerini eklemiyoruz
      // Sadece temel adres bilgisini CustomerData.Address alanına ekliyoruz
      customerData.Address = addressToUse || 'Osmanbey 1';
      customerData.CityCode = cityCodeToUse || 'TR.34'; // İstanbul varsayılan
      customerData.DistrictCode = districtCodeToUse || 'TR.03412'; // İstanbul-Fatih varsayılan
      
      // Backend'in beklediği formatta kimlik numarası veya vergi numarası ekle
      if (formData.isIndividual) {
        customerData.CustomerIdentityNumber = formData.identityNumber || '22222222222';
        customerData.IdentityNum = formData.identityNumber || '22222222222';
      } else {
        customerData.TaxNumber = formData.taxNumber || '1234567890';
        customerData.TaxOfficeCode = formData.taxOffice || '034104'; // İstanbul vergi dairesi kodu
      }
      
      // NOT: Backend'in CustomerWithTokenRequest modeli adres ve iletişim bilgilerini doğrudan içermiyor
      // Bu bilgiler müşteri oluşturduktan sonra ayrı API çağrılarıyla eklenecek
      // Bu nedenle adres ve iletişim bilgilerini CustomerData nesnesine eklemiyoruz
      
      // Form verilerini konsola yazdır
      console.log('Form verileri:', formData);
      console.log('Gönderilecek müşteri verisi:', customerData);
      
      // Token ile müşteri oluşturma - temel müşteri bilgilerini gönder
      const customerResponse = await customerService.createCustomerWithToken(token, customerData);
      
      if (customerResponse.success) {
        // Müşteri başarıyla oluşturuldu
        let createdCustomerCode = '';
        let responseToken = token; // Varsayılan olarak gönderilen token'i kullan
        
        // Backend'den dönen yanıtın formatını kontrol et
        const anyResponse = customerResponse as any;
        
        // Müşteri kodunu al
        if (anyResponse.data && anyResponse.data.customerCode) {
          createdCustomerCode = anyResponse.data.customerCode;
        } else if (anyResponse.customerCode) {
          createdCustomerCode = anyResponse.customerCode;
        } else if (anyResponse.data && anyResponse.data.data && anyResponse.data.data.customerCode) {
          createdCustomerCode = anyResponse.data.data.customerCode;
        }
        
        // Token'i al
        if (anyResponse.token) {
          responseToken = anyResponse.token;
          // Token'i localStorage'a kaydet
          localStorage.setItem('customerToken', responseToken);
          console.log('Token localStorage a kaydedildi:', responseToken.substring(0, 10) + '...');
        }
        
        console.log('Müşteri başarıyla oluşturuldu. Müşteri kodu:', createdCustomerCode);
        
        // Müşteri kodu ve token varsa adres ve iletişim bilgilerini ayrı API çağrıları ile ekle
        let adresEklendiMi = false;
        let iletisimEklendiMi = false;
        let telefonEklendiMi = false;
        let emailEklendiMi = false;
        
        if (createdCustomerCode && responseToken) {
          try {
            // Adres bilgilerini ekle
            if (formData.address) {
              const addressData = {
                AddressType: "1", // 1: İş adresi
                Address: formData.address,
                CityCode: formData.cityCode || cityCodeToUse || "34",
                DistrictCode: formData.districtCode || districtCodeToUse || "34-1",
                CountryCode: "TR", // Varsayılan olarak Türkiye
                IsDefault: true
              };
              
              console.log('Adres bilgileri gönderiliyor:', addressData);
              const addressResponse = await addCustomerAddress(
                responseToken, 
                createdCustomerCode, 
                addressData
              );
              
              adresEklendiMi = addressResponse.success;
              console.log('Adres ekleme sonucu:', adresEklendiMi ? 'Başarılı' : 'Başarısız', addressResponse);
            }
            
            // İletişim bilgilerini ekle
            // Telefon bilgisi varsa ekle
            if (formData.phone) {
              const phoneData = {
                CommunicationType: "1", // 1: Telefon
                CommunicationInfo: formData.phone,
                IsDefault: true
              };
              
              console.log('Telefon bilgisi gönderiliyor:', phoneData);
              const phoneResponse = await addCustomerCommunication(
                responseToken, 
                createdCustomerCode, 
                phoneData
              );
              
              telefonEklendiMi = phoneResponse.success;
              console.log('Telefon bilgisi ekleme sonucu:', phoneResponse.success ? 'Başarılı' : 'Başarısız', phoneResponse);
            }
              
            // E-posta bilgisi varsa ekle
            if (formData.email) {
              const emailData = {
                CommunicationType: "2", // 2: E-posta
                CommunicationInfo: formData.email,
                IsDefault: true
              };
              
              console.log('E-posta bilgisi gönderiliyor:', emailData);
              const emailResponse = await addCustomerCommunication(
                responseToken, 
                createdCustomerCode, 
                emailData
              );
              
              emailEklendiMi = emailResponse.success;
              console.log('E-posta bilgisi ekleme sonucu:', emailResponse.success ? 'Başarılı' : 'Başarısız', emailResponse);
            }
            // İletişim bilgilerinin durumunu güncelle
            iletisimEklendiMi = telefonEklendiMi || emailEklendiMi;
            
            // İletişim bilgisi yoksa başarılı say
            if (!formData.phone && !formData.email) {
              iletisimEklendiMi = true;
            }
            
            console.log('Adres ve iletişim bilgileri ekleme durumu:', 
              adresEklendiMi ? 'Adres eklendi' : 'Adres eklenemedi', 
              iletisimEklendiMi ? 'İletişim bilgileri eklendi' : 'İletişim bilgileri eklenemedi');
          } catch (error) {
            console.error('Adres veya iletişim bilgisi eklenirken hata oluştu:', error);
            // Ana müşteri kaydı başarılı olduğu için işleme devam et
          }
        }
        
        // Müşteri özelliklerini (attribute) varsayılanlarla otomatik kaydet
        if (createdCustomerCode) {
          try {
            await saveCustomerProperties(createdCustomerCode, DEFAULT_CUSTOMER_PROPERTIES);
            console.log('Varsayılan müşteri özellikleri kaydedildi.');
          } catch (propErr) {
            console.warn('Varsayılan müşteri özellikleri kaydedilemedi:', propErr);
          }
        }

        // Başarı durumunda konfeti ve ses efekti
        setShowConfetti(true);
        playSuccessSound();
        
        // 5 saniye sonra konfeti efektini kaldır
        setTimeout(() => setShowConfetti(false), 5000);
        // Kişi bilgilerini ekle
        let kisiEklendiMi = false;
        if (formData.firstName && formData.lastName && createdCustomerCode && responseToken) {
          try {
            const contactData = {
              ContactName: formData.firstName + " " + formData.lastName,
              ContactTitle: formData.contactTitle || "",
              ContactPhone: formData.phone || "",
              ContactEmail: formData.email || "",
              IsDefault: true
            };
            
            console.log('Kişi bilgisi gönderiliyor:', contactData);
            const contactResponse = await addCustomerContact(
              responseToken, 
              createdCustomerCode, 
              contactData
            );
            
            kisiEklendiMi = contactResponse.success;
            console.log('Kişi bilgisi ekleme sonucu:', kisiEklendiMi ? 'Başarılı' : 'Başarısız', contactResponse);
          } catch (error) {
            console.error('Kişi bilgisi eklenirken hata oluştu:', error);
            // Ana müşteri kaydı başarılı olduğu için işleme devam et
          }
        } else {
          // Kişi bilgisi yoksa başarılı say
          kisiEklendiMi = true;
        }
        
        // Bildirim mesajını oluştur
        let bildirimMesaji = 'Müşteri başarıyla oluşturuldu';
        let bildirimTipi: 'success' | 'error' = 'success';
        
        // Hata durumlarını kontrol et
        const hatalar = [];
        if (!adresEklendiMi) hatalar.push('adres');
        if (!iletisimEklendiMi) hatalar.push('iletişim');
        if (!kisiEklendiMi && formData.firstName && formData.lastName) hatalar.push('bağlantılı kişi');
        
        if (hatalar.length > 0) {
          bildirimMesaji += ', ancak ' + hatalar.join(' ve ') + ' bilgileri eklenemedi';
          bildirimTipi = 'error'; // warning yerine error kullan
        } else {
          bildirimMesaji += ' ve tüm bilgiler kaydedildi';
        }
        
        // Başarılı bildirim göster
        setSnackbar({
          open: true,
          message: bildirimMesaji,
          severity: bildirimTipi
        });
        
        // Form temizle ve yükleme durumunu kapat
        setFormData({
          formType: 'detailed',
          customerType: '3',
          isIndividual: false,
          country: 'TR',
          region: '',
          city: '',
          district: '',
          taxOffice: '',
          taxNumber: '',
          identityNum: '',
          firstName: '',
          lastName: '',
          customerName: '',
          address: '',
          authPhone: '',
          accountingPhone: '',
          authEmail: '',
          accountingEmail: '',
          contactName: '',
          isSubjectToEInvoice: false,
          eInvoiceStartDate: null,
          isSubjectToEDispatch: false,
          eDispatchStartDate: null,
          exchangeTypeCode: 'TRY',
          isShippingSameAsBilling: true,
          shippingRegion: '',
          shippingCity: '',
          shippingDistrict: '',
          shippingAddress: '',
          // Geriye dönük/yardımcı alanlar
          companyName: '',
          email: '',
          phone: '',
          stateCode: '',
          cityCode: '',
          districtCode: '',
          identityNumber: '',
          isSubjectToEShipment: false,
          eShipmentStartDate: null,
          termsAccepted: false,
          contactTitle: ''
        });
        
        // Yükleme durumunu kapat
        setIsLoading(false);
        
        // Müşteri listesine yönlendir
        setTimeout(() => {
          navigate('/customers');
        }, 1500); // 1.5 saniye sonra yönlendir (kullanıcının bildirimi görmesi için)
      } else {
        // Hata durumu
        console.error('Müşteri oluşturma hatası:', customerResponse);
        // Hata durumunda ses efekti çal
        playErrorSound();
        setSnackbar({
          open: true,
          message: `Müşteri oluşturulurken hata oluştu: ${customerResponse.message || 'Bilinmeyen hata'}`,
          severity: 'error'
        });
        setIsLoading(false);
      }
    } catch (error: any) {
      console.error('Müşteri kayıt hatası:', error);
      // Hata durumunda ses efekti çal
      playErrorSound();
      setSnackbar({
        open: true,
        message: `Müşteri oluşturulurken hata oluştu: ${error.message || 'Bilinmeyen hata'}`,
        severity: 'error'
      });
      setIsLoading(false);
    }
  };
  const resetForm = () => {
    setFormData({
      formType: 'detailed',
      customerType: '3',
      isIndividual: false,
      country: 'TR',
      region: '',
      city: '',
      district: '',
      taxOffice: '',
      taxNumber: '',
      identityNum: '',
      firstName: '',
      lastName: '',
      customerName: '',
      address: '',
      authPhone: '',
      accountingPhone: '',
      authEmail: '',
      accountingEmail: '',
      contactName: '',
      isSubjectToEInvoice: false,
      eInvoiceStartDate: null,
      isSubjectToEDispatch: false,
      eDispatchStartDate: null,
      exchangeTypeCode: 'TRY',
      isShippingSameAsBilling: true,
      shippingRegion: '',
      shippingCity: '',
      shippingDistrict: '',
      shippingAddress: '',
      // Geriye dönük/yardımcı alanlar
      companyName: '',
      email: '',
      phone: '',
      stateCode: '',
      cityCode: '',
      districtCode: '',
      identityNumber: '',
      isSubjectToEShipment: false,
      eShipmentStartDate: null,
      termsAccepted: false,
      contactTitle: ''
    });
    setSnackbar({
      open: true,
      message: t('customerRegistration.messages.formReset'),
      severity: 'success'
    });
  };

  // Snackbar'i kapat
  const handleCloseSnackbar = () => {
    setSnackbar({
      ...snackbar,
      open: false
    });
  };

  return (
    <Box sx={{ pb: isMobile ? '80px' : 0 }}>
      <Container maxWidth="md" disableGutters sx={{ 
        py: 4,
        px: isMobile ? 1 : 3
      }}>
        {showConfetti && (
          <ReactConfetti
            width={windowDimensions.width}
            height={windowDimensions.height}
            recycle={false}
          />
        )}
        <Paper elevation={3} sx={{ p: 3, mt: 3, mb: isMobile ? 10 : 3 }}>
          <Typography variant="h5" gutterBottom>
            {t('customerRegistration.title')}
          </Typography>
          <Box component="form" noValidate sx={{ mt: 2 }} onSubmit={handleSubmit} className="customer-form" style={{ opacity: tokenValidating || !tokenValid ? 0.5 : 1 }}>
          {/* Form içeriği token geçerli değilse devre dışı */}
          <fieldset disabled={tokenValidating || !tokenValid} style={{ border: 'none', padding: 0, margin: 0 }}>
            {/* Müşteri Ünvanı */}
            <Box className="form-row" sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 3 }}>
              <TextField
                required
                label={t('customerRegistration.form.customerName.label')}
                name="customerName"
                className="full-row"
                value={formData.customerName}
                onChange={handleInputChange}
                placeholder={t('customerRegistration.form.customerName.placeholder')}
                sx={{ flex: '1 1 100%' }}
              />
            </Box>

            {/* Switch ve Checkboxlar */}
            <Box className="form-row" sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 2, mb: 2 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.isIndividual}
                    onChange={(e) => setFormData({ ...formData, isIndividual: e.target.checked })}
                    name="isIndividual"
                  />
                }
                label={t('customerRegistration.form.customerType.individual')}
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={!!formData.isSubjectToEInvoice}
                    onChange={handleCheckboxChange}
                    name="isSubjectToEInvoice"
                  />
                }
                label={t('customerRegistration.form.isSubjectToEInvoice.label')}
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={!!formData.isSubjectToEDispatch}
                    onChange={handleCheckboxChange}
                    name="isSubjectToEDispatch"
                  />
                }
                label={t('customerRegistration.form.isSubjectToEDispatch.label')}
              />
            </Box>

            {/* E-fatura/E-İrsaliye tarih alanları */}
            {(formData.isSubjectToEInvoice || formData.isSubjectToEDispatch) && (
              <Box className="form-row" sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 3 }}>
                {formData.isSubjectToEInvoice && (
                  <TextField
                    type="date"
                    label={t('customerRegistration.form.eInvoiceStartDate.label')}
                    value={formData.eInvoiceStartDate ? formData.eInvoiceStartDate.toISOString().split('T')[0] : ''}
                    onChange={(e) => handleDateChange('eInvoiceStartDate', e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    fullWidth={isMobile}
                    sx={{ flex: '1 1 48%', minWidth: '200px' }}
                  />
                )}
                {formData.isSubjectToEDispatch && (
                  <TextField
                    type="date"
                    label={t('customerRegistration.form.eDispatchStartDate.label')}
                    value={formData.eDispatchStartDate ? formData.eDispatchStartDate.toISOString().split('T')[0] : ''}
                    onChange={(e) => handleDateChange('eDispatchStartDate', e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    fullWidth={isMobile}
                    sx={{ flex: '1 1 45%', minWidth: '200px' }}
                  />
                )}
              </Box>
            )}

            {/* Bireysel/Kurumsal blok */}
            {formData.formType === 'detailed' && formData.isIndividual && (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 3 }}>
                <TextField
                  fullWidth={isMobile}
                  required
                  label={t('customerRegistration.form.firstName.label')}
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleInputChange}
                  sx={{ flex: '1 1 48%', minWidth: '200px', width: isMobile ? '100%' : 'auto' }}
                />
                <TextField
                  fullWidth={isMobile}
                  required
                  label={t('customerRegistration.form.lastName.label')}
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleInputChange}
                  sx={{ flex: '1 1 45%', minWidth: '200px', width: isMobile ? '100%' : 'auto' }}
                />
                <TextField
                  fullWidth={isMobile}
                  required
                  label={t('customerRegistration.form.identityNumber.label')}
                  name="identityNumber"
                  value={formData.identityNumber}
                  onChange={handleInputChange}
                  placeholder={t('customerRegistration.form.identityNumber.placeholder')}
                  sx={{ flex: '1 1 100%', minWidth: '200px' }}
                />
              </Box>
            )}

            {formData.formType === 'detailed' && !formData.isIndividual && (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 3 }}>
                <FormControl fullWidth={isMobile} required sx={{ flex: '1 1 48%', minWidth: '200px', width: isMobile ? '100%' : 'auto' }}>
                  <InputLabel>{t('customerRegistration.form.taxOffice.label')}</InputLabel>
                  <Select
                    name="taxOffice"
                    value={formData.taxOffice || ''}
                    label={t('customerRegistration.form.taxOffice.label')}
                    onChange={handleSelectChange}
                    disabled={isLoadingTaxOfficesHook}
                  >
                    {isLoadingTaxOfficesHook ? (
                      <MenuItem value="" disabled>{t('customerRegistration.form.taxOffice.loading')}</MenuItem>
                    ) : taxOfficesDataFromHook && taxOfficesDataFromHook.length > 0 ? (
                      taxOfficesDataFromHook.map((office: any) => (
                        <MenuItem key={office.taxOfficeCode} value={office.taxOfficeCode}>
                          {office.taxOfficeDescription || office.taxOfficeCode}
                        </MenuItem>
                      ))
                    ) : (
                      <MenuItem value="" disabled>{t('customerRegistration.form.taxOffice.notFound')}</MenuItem>
                    )}
                  </Select>
                </FormControl>
                <TextField
                  fullWidth={isMobile}
                  required
                  label={t('customerRegistration.form.taxNumber.label')}
                  name="taxNumber"
                  value={formData.taxNumber}
                  onChange={handleInputChange}
                  sx={{ flex: '1 1 45%', minWidth: '200px', width: isMobile ? '100%' : 'auto' }}
                />
              </Box>
            )}

            <Divider sx={{ my: 2 }} />
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Box sx={{ width: '100%' }}>
                <Typography variant="subtitle1" gutterBottom>{t('customerRegistration.addressInfo')}</Typography>
                <Box className="form-row" sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2 }}>
                  <FormControl fullWidth={isMobile} required sx={{ flex: '1 1 48%', minWidth: '200px', width: isMobile ? '100%' : 'auto' }}>
                    <InputLabel>{t('customerRegistration.form.country.label')}</InputLabel>
                    <Select
                      name="country"
                      value={formData.country || ''}
                      label={t('customerRegistration.form.country.label')}
                      onChange={handleSelectChange}
                    >
                      {countriesDataFromHook && countriesDataFromHook.length > 0 ? (
                        countriesDataFromHook.map((c: any) => (
                          <MenuItem key={c.countryCode} value={c.countryCode}>
                            {c.countryDescription}
                          </MenuItem>
                        ))
                      ) : (
                        <MenuItem value="TR">{t('customerRegistration.countries.turkey')}</MenuItem>
                      )}
                    </Select>
                  </FormControl>
                  {formData.country === 'TR' && (
                    <>
                      <FormControl fullWidth={isMobile} sx={{ flex: '1 1 48%', minWidth: '200px', width: isMobile ? '100%' : 'auto' }}>
                        <InputLabel>{t('customerRegistration.form.region.label')}</InputLabel>
                        <Select
                          name="region"
                          value={formData.region || ''}
                          label={t('customerRegistration.form.region.label')}
                          onChange={handleSelectChange}
                        >
                          {regions && regions.length > 0 ? (
                            regions.map((r) => (
                              <MenuItem key={r.code} value={r.code}>
                                {r.name || r.code}
                              </MenuItem>
                            ))
                          ) : (
                            <MenuItem value="" disabled>{t('customerRegistration.form.region.notFound')}</MenuItem>
                          )}
                        </Select>
                      </FormControl>
                      <FormControl fullWidth={isMobile} sx={{ flex: '1 1 48%', minWidth: '200px' }}>
                        <InputLabel>{t('customerRegistration.form.city.label')}</InputLabel>
                        <Select
                          name="city"
                          value={formData.city || ''}
                          label={t('customerRegistration.form.city.label')}
                          onChange={handleSelectChange}
                        >
                          {cities.map((c) => (
                            <MenuItem key={c.code} value={c.code}>{c.name}</MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                      <FormControl fullWidth={isMobile} sx={{ flex: '1 1 45%', minWidth: '200px' }}>
                        <InputLabel>{t('customerRegistration.form.district.label')}</InputLabel>
                        <Select
                          name="district"
                          value={formData.district || ''}
                          label={t('customerRegistration.form.district.label')}
                          onChange={handleSelectChange}
                        >
                          {districts.map((d) => (
                            <MenuItem key={d.code} value={d.code}>{d.name}</MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </>
                  )}
                  <TextField
                    fullWidth={isMobile}
                    label={t('customerRegistration.form.address.label')}
                    name="address"
                    className="full-row"
                    value={formData.address}
                    onChange={handleInputChange}
                    multiline
                    minRows={2}
                    sx={{ flex: '1 1 100%', minWidth: '200px', width: '100%' }}
                  />
                </Box>
              </Box>

          </Box>

          {/* Teslim Adresi (Fatura Adresi ile aynı) */}
          <Box sx={{ mb: 2 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={formData.isShippingSameAsBilling}
                  onChange={(e) => setFormData(prev => ({ ...prev, isShippingSameAsBilling: e.target.checked }))}
                  name="isShippingSameAsBilling"
                />
              }
              label={t('customerRegistration.form.shipping.sameAsBilling') || 'Teslim Adresi (Fatura Adresi ile aynı)'}
            />
          </Box>
          {!formData.isShippingSameAsBilling && (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2 }}>
              <FormControl fullWidth={isMobile} sx={{ flex: '1 1 48%', minWidth: '200px', width: isMobile ? '100%' : 'auto' }}>
                <InputLabel>{t('customerRegistration.form.region.label')}</InputLabel>
                <Select
                  name="shippingRegion"
                  value={formData.shippingRegion || ''}
                  label={t('customerRegistration.form.region.label')}
                  onChange={handleSelectChange}
                >
                  {regions && regions.length > 0 ? (
                    regions.map((r) => (
                      <MenuItem key={r.code} value={r.code}>
                        {r.name || r.code}
                      </MenuItem>
                    ))
                  ) : (
                    <MenuItem value="" disabled>{t('customerRegistration.form.region.notFound')}</MenuItem>
                  )}
                </Select>
              </FormControl>
              <FormControl fullWidth={isMobile} sx={{ flex: '1 1 48%', minWidth: '200px' }}>
                <InputLabel>{t('customerRegistration.form.city.label')}</InputLabel>
                <Select
                  name="shippingCity"
                  value={formData.shippingCity || ''}
                  label={t('customerRegistration.form.city.label')}
                  onChange={handleSelectChange}
                >
                  {shippingCities.map((c) => (
                    <MenuItem key={c.code} value={c.code}>{c.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl fullWidth={isMobile} sx={{ flex: '1 1 48%', minWidth: '200px' }}>
                <InputLabel>{t('customerRegistration.form.district.label')}</InputLabel>
                <Select
                  name="shippingDistrict"
                  value={formData.shippingDistrict || ''}
                  label={t('customerRegistration.form.district.label')}
                  onChange={handleSelectChange}
                >
                  {shippingDistricts.map((d) => (
                    <MenuItem key={d.code} value={d.code}>{d.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField
                fullWidth={isMobile}
                label={t('customerRegistration.form.address.label')}
                name="shippingAddress"
                className="full-row"
                value={formData.shippingAddress}
                onChange={handleInputChange}
                multiline
                minRows={2}
                sx={{ flex: '1 1 100%', minWidth: '200px', width: '100%' }}
              />
            </Box>
          )}

          {/* Para Birimi + Müşteri Özellikleri */}
          <Box className="form-row" sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 2, mt: 2, mb: 3 }}>
            <FormControl fullWidth={isMobile} sx={{ flex: '1 1 48%', minWidth: '200px', width: isMobile ? '100%' : 'auto' }}>
              <InputLabel>{t('customerRegistration.form.exchangeTypeCode.label')}</InputLabel>
              <Select
                name="exchangeTypeCode"
                value={formData.exchangeTypeCode}
                label={t('customerRegistration.form.exchangeTypeCode.label')}
                onChange={handleSelectChange}
              >
                {isLoadingCurrenciesHook ? (
                  <MenuItem value="" disabled>{t('customerRegistration.form.exchangeTypeCode.loading')}</MenuItem>
                ) : currenciesDataFromHook && currenciesDataFromHook.length > 0 ? (
                  currenciesDataFromHook.map((currency: any) => (
                    <MenuItem key={currency.currencyCode} value={currency.currencyCode}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
                        <span><strong>{currency.currencyCode}</strong> - {currency.currencyDescription}</span>
                      </Box>
                    </MenuItem>
                  ))
                ) : (
                  <MenuItem value="" disabled>{t('customerRegistration.form.exchangeTypeCode.notFound')}</MenuItem>
                )}
              </Select>
              </FormControl>
              <Button 
                variant="contained" 
                color="primary"
                onClick={() => (formData.region || formData.stateCode) && setIsCustomerPropertiesModalOpen(true)}
                disabled={!(formData.region || formData.stateCode)}
                title={(formData.region || formData.stateCode) ? '' : (t('customerRegistration.form.customerProperties.disabled') as string)}
              >
                {t('customerRegistration.form.customerProperties.label')}
              </Button>
            </Box>

            {/* Müşteri Özellikleri Modal */}
            <CustomerPropertiesModal
              open={isCustomerPropertiesModalOpen}
              onClose={() => setIsCustomerPropertiesModalOpen(false)}
              customerCode="temp"
              regionCode={(formData.region || formData.stateCode) as string}
              onSave={(payload: any) => {
                const cp: Record<number, string> = {};
                Object.entries(payload.CustomerProperties || {}).forEach(([k, v]) => {
                  const nk = parseInt(k as string, 10);
                  if (!Number.isNaN(nk)) cp[nk] = String(v ?? '');
                });
                setSelectedCustomerProperties(cp);
                setIsCustomerPropertiesModalOpen(false);
              }}
            />

            <Divider sx={{ my: 2 }} />
            <Typography variant="subtitle1" gutterBottom>{t('customerRegistration.communicationInfo')}</Typography>
            <Box className="form-row" sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 3 }}>
              <TextField fullWidth={isMobile} required label={t('customerRegistration.form.authPhone.label')} name="authPhone" value={formData.authPhone} onChange={handleInputChange} placeholder={t('customerRegistration.form.authPhone.placeholder')} sx={{ flex: '1 1 48%', minWidth: '200px', width: isMobile ? '100%' : 'auto' }} />
              <TextField fullWidth={isMobile} required label={t('customerRegistration.form.authEmail.label')} name="authEmail" type="email" value={formData.authEmail} onChange={handleInputChange} placeholder={t('customerRegistration.form.authEmail.placeholder')} sx={{ flex: '1 1 48%', minWidth: '200px', width: isMobile ? '100%' : 'auto' }} />
              <TextField fullWidth={isMobile} label={t('customerRegistration.form.accountingPhone.label')} name="accountingPhone" value={formData.accountingPhone} onChange={handleInputChange} placeholder={t('customerRegistration.form.accountingPhone.placeholder')} sx={{ flex: '1 1 48%', minWidth: '200px', width: isMobile ? '100%' : 'auto' }} />
              <TextField fullWidth={isMobile} label={t('customerRegistration.form.accountingEmail.label')} name="accountingEmail" type="email" value={formData.accountingEmail} onChange={handleInputChange} placeholder={t('customerRegistration.form.accountingEmail.placeholder')} sx={{ flex: '1 1 48%', minWidth: '200px', width: isMobile ? '100%' : 'auto' }} />
            </Box>

            {/* Mobil boşluk ve aksiyon butonları */}
            {isMobile && <Box sx={{ height: '180px' }} />}
            {!isMobile && (
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
                <Button type="button" variant="outlined" sx={{ mr: 1 }} onClick={() => navigate('/customers')}>
                  {t('customerRegistration.buttons.cancel')}
                </Button>
                <Button type="submit" variant="contained" color="primary" disabled={isLoading}>
                  {isLoading ? <CircularProgress size={24} /> : t('customerRegistration.buttons.submit')}
                </Button>
              </Box>
            )}

            {/* Mobil için sabit aksiyon çubuğu */}
            {isMobile && (
              <Paper elevation={6} sx={{ position: 'fixed', left: 0, right: 0, bottom: `calc(82px + env(safe-area-inset-bottom, 0px))`, zIndex: 10050, p: 1.5, mx: 'auto', maxWidth: 'md', backgroundColor: 'background.paper', borderTopLeftRadius: 12, borderTopRightRadius: 12 }}>
                <Box sx={{ display: 'flex', gap: 1.5 }}>
                  <Button type="button" variant="outlined" fullWidth onClick={() => navigate('/customers')}>
                    {t('customerRegistration.buttons.cancel')}
                  </Button>
                  <Button type="submit" variant="contained" color="primary" fullWidth disabled={isLoading}>
                    {isLoading ? <CircularProgress size={24} /> : t('customerRegistration.buttons.submit')}
                  </Button>
                </Box>
              </Paper>
            )}
          </fieldset>
        </Box>
      </Paper>
      </Container>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default CustomerRegistration;
